/**
 * CR-8598: the platform password policy, for live feedback while typing.
 *
 * The server is the authority — every rule here is enforced again by
 * `AUTH_PASSWORD_VALIDATORS` (backend/core/password_validators.py) — but the
 * rules a browser can evaluate are mirrored so a checklist can tick as the
 * user types. Change both together.
 */
import type { PasswordRuleKey } from './api';

export type { PasswordRuleKey };

/** Shortest password the policy accepts. */
export const PASSWORD_MIN_LENGTH = 12;

/** The wording every password form lists, in display order. */
export const PASSWORD_RULE_LABELS: Record<PasswordRuleKey, string> = {
  length: `At least ${PASSWORD_MIN_LENGTH} characters`,
  letters_and_numbers: 'Contains letters and numbers',
  personal_info: "Doesn't contain your name, email address or company name",
  not_breached: "Hasn't appeared in a known data breach",
};

/** Display order of the policy's requirements. */
export const PASSWORD_RULE_ORDER: PasswordRuleKey[] = [
  'length', 'letters_and_numbers', 'personal_info', 'not_breached',
];

/** Label for the confirm-password row shown alongside the policy rules. */
export const PASSWORDS_MATCH_LABEL = 'Passwords match';

/** Shortest name, email or company piece the personal-information rule checks. */
const MIN_TOKEN_LENGTH = 3;

/** Legal suffixes and filler words that say nothing about a company. */
const COMPANY_NAME_IGNORED_WORDS = new Set([
  'ltd', 'limited', 'plc', 'llc', 'llp', 'inc', 'incorporated', 'corp',
  'corporation', 'company', 'gmbh', 'pty', 'srl', 'spa', 'sas', 'the', 'and',
  'usa',
]);

const NON_ALNUM = /[^\p{L}\p{N}]+/gu;

/** Who a password belongs to — what it must not contain. */
export interface PasswordOwner {
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  companyNames?: (string | null | undefined)[];
}

function tokens(value: string | null | undefined, whole: boolean, ignored?: Set<string>): string[] {
  const lowered = (value ?? '').toLowerCase();
  const pieces = lowered.split(NON_ALNUM).filter((p) => p && !ignored?.has(p));
  if (whole) pieces.push(lowered.replace(NON_ALNUM, ''));
  return pieces.filter((p) => p.length >= MIN_TOKEN_LENGTH);
}

/**
 * Every name, email and company piece a password may not contain.
 *
 * @param owner - The person the password is for
 * @returns Lower-cased pieces of three or more characters
 */
export function personalInfoTokens(owner: PasswordOwner): string[] {
  const email = owner.email ?? '';
  const localPart = email.includes('@') ? email.slice(0, email.lastIndexOf('@')) : email;
  return Array.from(new Set([
    ...tokens(localPart, true),
    ...tokens(owner.firstName, true),
    ...tokens(owner.lastName, true),
    ...(owner.companyNames ?? []).flatMap((name) => tokens(name, false, COMPANY_NAME_IGNORED_WORDS)),
  ]));
}

/**
 * Whether a password contains any of the owner's name, email or company pieces.
 *
 * @param password - The candidate password
 * @param owner - The person the password is for
 * @returns True when the password contains personal information
 */
export function containsPersonalInfo(password: string, owner: PasswordOwner): boolean {
  const lowered = password.toLowerCase();
  const compact = lowered.replace(NON_ALNUM, '');
  return personalInfoTokens(owner).some((t) => lowered.includes(t) || compact.includes(t));
}

/**
 * Whether a password has at least one letter and at least one digit.
 *
 * @param password - The candidate password
 * @returns True when both a letter and a digit are present
 */
export function hasLettersAndNumbers(password: string): boolean {
  return /\p{L}/u.test(password) && /\p{Nd}/u.test(password);
}

/**
 * The rules a browser can evaluate. `not_breached` needs the server, so it is
 * absent here; `personal_info` only when the owner is known.
 *
 * @param password - The candidate password
 * @param owner - The person the password is for, when the page knows them
 * @returns Pass/fail per locally checkable rule
 */
export function localPasswordRules(
  password: string,
  owner?: PasswordOwner,
): Partial<Record<PasswordRuleKey, boolean>> {
  const rules: Partial<Record<PasswordRuleKey, boolean>> = {
    length: password.length >= PASSWORD_MIN_LENGTH,
    letters_and_numbers: hasLettersAndNumbers(password),
  };
  if (owner) rules.personal_info = !containsPersonalInfo(password, owner);
  return rules;
}

/**
 * Where one requirement stands for the password typed so far.
 * `pending` = not answerable yet (nothing typed, or only checked on save).
 */
export type PasswordRequirementStatus = 'met' | 'unmet' | 'checking' | 'pending';

/** One checklist row. */
export interface PasswordRequirementItem {
  key: PasswordRuleKey | 'match';
  label: string;
  status: PasswordRequirementStatus;
}

/** What a password form knows beyond the password itself. */
export interface PasswordRequirementOptions {
  /** Who the password is for; enables the local name/email/company check. */
  owner?: PasswordOwner;
  /** The confirm field's value; adds a "Passwords match" row when given. */
  confirm?: string;
  /**
   * True on a page with a live server check: personal information and breach
   * rows then wait for `server` instead of being judged locally / on save.
   */
  live?: boolean;
  /** The server's answer for exactly this password, once it has one. */
  server?: Record<PasswordRuleKey, boolean> | null;
  /** The server check for this password is in flight. */
  checking?: boolean;
}

const SERVER_RULES: PasswordRuleKey[] = ['personal_info', 'not_breached'];

/**
 * The checklist rows for a password form, in display order.
 *
 * A row is red as soon as either the browser or the server says the password
 * breaks it, green once every check that applies agrees it is met.
 *
 * @param password - What the user has typed
 * @param options - Owner, confirmation, and the live server answer if any
 * @returns One row per policy rule, plus "Passwords match" when confirming
 * @example
 * const items = passwordRequirementItems(password, { confirm, owner });
 * const ready = allRequirementsMet(items);
 */
export function passwordRequirementItems(
  password: string,
  { owner, confirm, live = false, server = null, checking = false }: PasswordRequirementOptions = {},
): PasswordRequirementItem[] {
  const local = localPasswordRules(password, owner);
  const items: PasswordRequirementItem[] = PASSWORD_RULE_ORDER.map((key) => {
    const label = PASSWORD_RULE_LABELS[key];
    if (!password) return { key, label, status: 'pending' };
    const localOk = local[key];
    const serverOk = server?.[key];
    if (localOk === false || serverOk === false) return { key, label, status: 'unmet' };
    const needsServer = live ? SERVER_RULES.includes(key) : localOk === undefined;
    if (needsServer && serverOk === undefined) {
      return { key, label, status: live && checking ? 'checking' : 'pending' };
    }
    return { key, label, status: 'met' };
  });
  if (confirm !== undefined) {
    const status: PasswordRequirementStatus = !confirm ? 'pending' : confirm === password ? 'met' : 'unmet';
    items.push({ key: 'match', label: PASSWORDS_MATCH_LABEL, status });
  }
  return items;
}

/**
 * Whether every row is met.
 *
 * @param items - Rows from {@link passwordRequirementItems}
 * @returns True when nothing is unmet, pending or still checking
 */
export function allRequirementsMet(items: PasswordRequirementItem[]): boolean {
  return items.every((item) => item.status === 'met');
}

/**
 * Whether every rule the browser can judge is met (skips rows that wait on
 * the server). Used when the server check is unavailable: the server still
 * enforces the full policy on submit.
 *
 * @param items - Rows from {@link passwordRequirementItems}
 * @returns True when no row is unmet or checking, ignoring server-only rows
 */
export function localRequirementsMet(items: PasswordRequirementItem[]): boolean {
  return items.every((item) =>
    item.status === 'met' || (item.status === 'pending' && SERVER_RULES.includes(item.key as PasswordRuleKey)),
  );
}
